import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Card, CardBody, Tab, Tabs } from '@nextui-org/react';
import { useTranslation } from 'common/src/hooks/useTranslation';
import useUserStore from 'common/src/stores/useUserStore';
import { getChartThemeColors } from 'common/src/utils/chartTheme';
import { CHINESE_NEW_YEARS } from 'common/src/utils/definitions';
import { getIndustryVolumeCurves } from 'common/src/api/industryVolume';
import MultiSelectWithActions from '../common/MultiSelectWithActions';
import { EmptyState } from '../ui';
import {
  OTHERS,
  curveSeries,
  weeksFromChineseNewYear,
  type CurveMode,
  type CurveRow,
  type PulseData,
} from '../../utils/industry-volume/build-pulse';
import { seasonShort, thousands, week } from './format';
import SearchSelect from './SearchSelect';
import { useVolumeUnit } from './unit';

// La temporada actual resalta; las anteriores, en colores distinguibles.
const CURRENT_COLOR = '#7C3AED';
const PAST_COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#EC4899', '#14B8A6', '#EF4444', '#84CC16', '#06B6D4'];
const CHERRY_SPECIES_ID = 2;

type Axis = 'week' | 'cny';

const newYearOf = (season: string): Date | undefined =>
  (CHINESE_NEW_YEARS as Record<string, Date>)[`season${season.split('-')[1]}`];

/** Avance de la temporada: la curva de cada temporada por semana, la actual hasta el corte. */
export default function VolumeCurveCard({
  data,
  weeks,
  speciesId,
}: {
  data: PulseData;
  weeks: number[];
  speciesId: number;
}) {
  const { t } = useTranslation('volume');
  const { darkMode } = useUserStore();
  const colors = getChartThemeColors(darkMode);
  const { toUnit, label } = useVolumeUnit();
  const u = t(label);
  const byArrival = data.basis === 'eta';

  const seasons = useMemo(
    () => Array.from(new Set((data.curves ?? []).map((c) => c.s))).sort().reverse(),
    [data.curves]
  );
  // Por defecto las cuatro últimas (la actual y las tres anteriores).
  const [selected, setSelected] = useState<string[]>(() =>
    seasons.filter((s) => s <= data.season).slice(0, 4)
  );
  const [varieties, setVarieties] = useState<string[]>([]);
  const [mode, setMode] = useState<CurveMode>('cumulative');
  const [axis, setAxis] = useState<Axis>('week');
  const canUseNewYear = speciesId === CHERRY_SPECIES_ID;

  // Todas las variedades con volumen, de la más grande a la menor.
  const varietyOptions = useMemo(
    () =>
      [
        ...(data.varieties ?? []).filter((x) => x.v !== OTHERS),
        ...(data.varieties_other ?? []),
      ]
        .sort((a, b) => (b.cur ?? 0) - (a.cur ?? 0))
        .map((x) => ({ key: x.v, label: x.v })),
    [data.varieties, data.varieties_other]
  );
  // Las 9 principales vienen en el pulso; si se elige alguna otra, la suma se pide aparte.
  const top = data.top_varieties ?? [];
  const inPulse = varieties.every((v) => top.includes(v));
  const [rows, setRows] = useState<CurveRow[] | null>(null);
  const varietiesKey = varieties.join('|');
  useEffect(() => {
    setRows(null);
    if (inPulse) return;
    let active = true;
    getIndustryVolumeCurves(speciesId, weeks, varieties, data.basis ?? 'etd')
      .then((result) => active && setRows(result.map(([s, i, k]) => ({ s, i, k }))))
      .catch(() => active && setRows([]));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inPulse, varietiesKey, speciesId, weeks, data.basis]);

  // Desde el pulso: una serie por variedad elegida, sumadas.
  const pulseRows = useMemo(
    () =>
      varieties.length
        ? (data.curves ?? []).filter((c) => varieties.includes(c.v)).map(({ s, i, k }) => ({ s, i, k }))
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.curves, varietiesKey]
  );
  const series = curveSeries(data, {
    seasons: [...selected].sort(),
    variety: null,
    mode,
    weeks: weeks.length,
    rows: inPulse ? pulseRows : (rows ?? []),
  });

  // Eje vs Año Nuevo Chino: cada temporada se corre a su ANC (0 = semana del ANC).
  const useNewYear = canUseNewYear && axis === 'cny';
  const plotted = useNewYear
    ? series.filter((s) => newYearOf(s.season))
    : series;
  const offsets = new Map(
    plotted.map((s) => [s.season, useNewYear ? weeksFromChineseNewYear(s.season, weeks, newYearOf(s.season)!) : []])
  );
  const relWeeks = useNewYear
    ? Array.from(new Set(Array.from(offsets.values()).flat())).sort((a, b) => a - b)
    : [];
  const categories = useNewYear
    ? relWeeks.map((r) => (r === 0 ? t('ANC') : r > 0 ? `+${r}` : String(r)))
    : weeks.map((w) => week(w));
  const valuesOf = (s: (typeof series)[number]) => {
    if (!useNewYear) return s.values;
    const rel = offsets.get(s.season)!;
    const byRel = new Map(rel.map((r, i) => [r, s.values[i]]));
    return relWeeks.map((r) => byRel.get(r) ?? null);
  };
  let pastIndex = 0;
  const seriesColors = plotted.map((s) =>
    s.isCurrent ? CURRENT_COLOR : PAST_COLORS[pastIndex++ % PAST_COLORS.length]
  );
  const cutoffWeek = data.cutoff_index ? weeks[data.cutoff_index - 1] : null;
  const cutoffX = (() => {
    if (!data.cutoff_index) return null;
    if (!useNewYear) return week(cutoffWeek);
    const rel = offsets.get(data.season)?.[data.cutoff_index - 1];
    return rel == null ? null : rel === 0 ? t('ANC') : rel > 0 ? `+${rel}` : String(rel);
  })();

  const options: ApexOptions = {
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, foreColor: colors.foreColor, background: 'transparent' },
    theme: { mode: darkMode ? 'dark' : 'light' },
    colors: seriesColors,
    stroke: { width: plotted.map((s) => (s.isCurrent ? 3 : 2)), curve: 'smooth' },
    grid: { borderColor: colors.gridBorderColor },
    xaxis: {
      categories,
      title: {
        text: useNewYear
          ? t('Semanas vs Año Nuevo Chino (0 = semana del ANC)')
          : byArrival
            ? t('Semana de arribo (ETA)')
            : t('Semana de zarpe (ETD)'),
      },
      // En celular no caben todas las semanas: se ocultan las que se pisan.
      labels: { rotate: 0, hideOverlappingLabels: true },
    },
    yaxis: {
      labels: { formatter: (value) => thousands(value) },
      title: { text: u },
    },
    tooltip: {
      shared: true,
      y: { formatter: (value) => (value == null ? '—' : `${thousands(value)} ${u}`) },
    },
    legend: { position: 'bottom' },
    annotations: cutoffX
      ? {
          xaxis: [
            {
              x: cutoffX,
              borderColor: CURRENT_COLOR,
              strokeDashArray: 4,
              label: {
                text: t('sem {{week}}', { week: week(cutoffWeek) }),
                style: { color: '#fff', background: CURRENT_COLOR },
              },
            },
          ],
        }
      : undefined,
  };
  const loadingVarieties = !inPulse && rows == null;

  return (
    <Card shadow='sm'>
      <CardBody className='gap-4 p-6'>
        <div>
          <h3 className='text-lg font-semibold'>{t('Avance de la temporada')}</h3>
          <p className='text-sm text-default-500'>
            {byArrival
              ? t('Volumen exportado por semana de arribo, acumulado o semanal. Temporada actual hasta la semana {{week}}; temporadas anteriores completas.', { week: week(data.cutoff_week) })
              : t('Volumen exportado por semana de zarpe, acumulado o semanal. Temporada actual hasta la semana {{week}}; temporadas anteriores completas.', { week: week(data.cutoff_week) })}
          </p>
        </div>
        <div className='flex flex-wrap items-end gap-3'>
          <MultiSelectWithActions
            size='sm'
            className='w-56'
            label={t('Temporadas')}
            options={seasons.map((s) => ({ value: s, label: s }))}
            selectedKeys={selected}
            onSelectionChange={(keys) => setSelected(keys.length ? keys : [data.season])}
          />
          <SearchSelect
            multiple
            label={t('Variedad')}
            allLabel={t('Todas las variedades')}
            options={varietyOptions}
            selected={varieties}
            onChange={setVarieties}
          />
          <Tabs size='sm' aria-label={t('Vista')} selectedKey={mode} onSelectionChange={(k) => setMode(k as CurveMode)}>
            <Tab key='cumulative' title={t('Acumulado')} />
            <Tab key='weekly' title={t('Semanal')} />
          </Tabs>
          {canUseNewYear && (
            <Tabs size='sm' aria-label={t('Eje')} selectedKey={axis} onSelectionChange={(k) => setAxis(k as Axis)}>
              <Tab key='week' title={t('Semana del año')} />
              <Tab key='cny' title={t('vs Año Nuevo Chino')} />
            </Tabs>
          )}
        </div>
        {plotted.length ? (
          <ReactApexChart
            key={`${label}|${axis}`}
            type='line'
            height={340}
            options={options}
            series={plotted.map((s) => ({
              name: s.isCurrent ? `${seasonShort(s.season)} (${t('hasta sem {{week}}', { week: week(data.cutoff_week) })})` : seasonShort(s.season),
              data: valuesOf(s).map((x) => (x == null ? null : toUnit(x))),
            }))}
          />
        ) : (
          <EmptyState title={loadingVarieties ? t('Cargando…') : t('No hay datos para esta selección.')} />
        )}
        {useNewYear && plotted.length < series.length && (
          <p className='text-xs text-default-500'>
            {t('Sin fecha de Año Nuevo Chino para algunas temporadas elegidas: no se muestran en este eje.')}
          </p>
        )}
      </CardBody>
    </Card>
  );
}
